// components/ConnectionTypes/ConnectionTypes.js
import React, { useState, useEffect } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import apiClient from '../../lib/apiClient';
import './ConnectionTypes.css';

function ConnectionTypes() {
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [connectionTypesLoaded, setConnectionTypesLoaded] = useState(false);
  const [editingConnectionType, setEditingConnectionType] = useState(null);
  const [connectionTypeForm, setConnectionTypeForm] = useState({
    type_id: '',
    name: '',
    color: '#3b82f6',
    description: '',
    icon: '🔗',
    display_order: 0
  });
  const [showConnectionTypeModal, setShowConnectionTypeModal] = useState(false);

  useEffect(() => {
    fetchConnectionTypes();
  }, []);

  const fetchConnectionTypes = async () => {
    try {
      const data = await apiClient.get('/connection-types');
      const sortedData = data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setConnectionTypes(sortedData);
      setConnectionTypesLoaded(true);
    } catch (error) {
      console.error('Error fetching connection types:', error);
      setConnectionTypesLoaded(true);
    }
  };

  const handleAddConnectionType = async () => {
    if (!connectionTypeForm.type_id || !connectionTypeForm.name || !connectionTypeForm.color) {
      alert('Type ID, Name, and Color are required');
      return;
    }

    try {
      await apiClient.post('/connection-types', connectionTypeForm);
      await fetchConnectionTypes();
      resetConnectionTypeForm();
      setShowConnectionTypeModal(false);
    } catch (error) {
      console.error('Error adding connection type:', error);
      alert(error.message || 'Failed to add connection type');
    }
  };

  const handleUpdateConnectionType = async () => {
    if (!editingConnectionType) return;

    try {
      await apiClient.put(`/connection-types/${editingConnectionType.id}`, connectionTypeForm);
      await fetchConnectionTypes();
      resetConnectionTypeForm();
      setShowConnectionTypeModal(false);
      setEditingConnectionType(null);
    } catch (error) {
      console.error('Error updating connection type:', error);
      alert(error.message || 'Failed to update connection type');
    }
  };

  const handleDeleteConnectionType = async (typeId) => {
    if (!(await confirmDialog('Are you sure you want to delete this connection type?'))) return;

    try {
      await apiClient.del(`/connection-types/${typeId}`);
      await fetchConnectionTypes();
    } catch (error) {
      console.error('Error deleting connection type:', error);
      alert(error.message || 'Failed to delete connection type');
    }
  };

  const handleEditConnectionType = (type) => {
    setEditingConnectionType(type);
    setConnectionTypeForm({
      type_id: type.type_id,
      name: type.name,
      color: type.color,
      description: type.description || '',
      icon: type.icon || '🔗',
      display_order: type.display_order || 0
    });
    setShowConnectionTypeModal(true);
  };

  const resetConnectionTypeForm = () => {
    setConnectionTypeForm({
      type_id: '',
      name: '',
      color: '#3b82f6',
      description: '',
      icon: '🔗',
      display_order: 0
    });
    setEditingConnectionType(null);
  };

  return (
    <div className="content-area">
      <h2 className="page-title">Connection Types</h2>
      
      <div className="connection-types-header">
        <p className="page-description">
          Manage the types of connections that can be created between technology components.
        </p>
        <button 
          className="add-button"
          onClick={() => {
            resetConnectionTypeForm();
            setShowConnectionTypeModal(true);
          }}
        >
          ➕ Add Connection Type
        </button>
      </div>

      {!connectionTypesLoaded ? (
        <div className="empty-state-card">
          <p className="empty-state-text">Loading connection types...</p>
        </div>
      ) : (
        <>
          <div className="connection-types-grid">
            {connectionTypes.map(type => (
              <div key={type.id} className="connection-type-card">
                <div className="connection-type-card-header">
                  <div 
                    className="connection-type-icon-large"
                    style={{ backgroundColor: type.color }}
                  >
                    {type.icon}
                  </div>
                  <div className="connection-type-card-info">
                    <h3 className="connection-type-card-name">{type.name}</h3>
                    <p className="connection-type-card-id">ID: {type.type_id}</p>
                  </div>
                  <div className="connection-type-card-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleEditConnectionType(type)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteConnectionType(type.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="connection-type-card-description">
                  {type.description || 'No description provided'}
                </p>
                <div className="connection-type-card-footer">
                  <span className="connection-type-color-badge" style={{ backgroundColor: type.color }}>
                    {type.color}
                  </span>
                  <span className="connection-type-order">Order: {type.display_order || 0}</span>
                </div>
              </div>
            ))}
          </div>

          {connectionTypes.length === 0 && (
            <div className="empty-state-card">
              <h3 className="empty-state-title">No Connection Types</h3>
              <p className="empty-state-text">
                Add connection types to define how technology components can be related.
              </p>
            </div>
          )}
        </>
      )}

      {showConnectionTypeModal && (
        <div className="modal-overlay" onClick={() => setShowConnectionTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              {editingConnectionType ? 'Edit Connection Type' : 'Add Connection Type'}
            </h3>
            
            <div className="modal-form">
              <label className="form-label">
                Type ID
                <input 
                  type="text"
                  value={connectionTypeForm.type_id}
                  onChange={(e) => setConnectionTypeForm({
                    ...connectionTypeForm,
                    type_id: e.target.value.toLowerCase().replace(/\s+/g, '_')
                  })}
                  placeholder="e.g., connects_to"
                  className="form-input"
                  disabled={editingConnectionType !== null}
                />
                <span className="form-hint">Unique identifier (lowercase, underscores only)</span>
              </label>
              
              <label className="form-label">
                Name
                <input 
                  type="text"
                  value={connectionTypeForm.name}
                  onChange={(e) => setConnectionTypeForm({
                    ...connectionTypeForm,
                    name: e.target.value
                  })}
                  placeholder="e.g., Network Connection"
                  className="form-input"
                />
              </label>
              
              <label className="form-label">
                Description
                <textarea 
                  value={connectionTypeForm.description}
                  onChange={(e) => setConnectionTypeForm({
                    ...connectionTypeForm,
                    description: e.target.value
                  })}
                  placeholder="Describe this connection type..."
                  rows="3"
                  className="form-textarea"
                />
              </label>
              
              <div className="form-row">
                <label className="form-label">
                  Icon
                  <input 
                    type="text"
                    value={connectionTypeForm.icon}
                    onChange={(e) => setConnectionTypeForm({
                      ...connectionTypeForm,
                      icon: e.target.value
                    })}
                    placeholder="🔗"
                    className="form-input"
                  />
                  <span className="form-hint">Emoji or symbol</span>
                </label>
                
                <label className="form-label">
                  Color
                  <input 
                    type="color"
                    value={connectionTypeForm.color}
                    onChange={(e) => setConnectionTypeForm({
                      ...connectionTypeForm,
                      color: e.target.value
                    })}
                    className="form-color-input"
                  />
                </label>
                
                <label className="form-label">
                  Display Order
                  <input 
                    type="number"
                    value={connectionTypeForm.display_order}
                    onChange={(e) => setConnectionTypeForm({
                      ...connectionTypeForm,
                      display_order: parseInt(e.target.value) || 0
                    })}
                    className="form-input"
                  />
                </label>
              </div>
              
              <div className="connection-type-preview-box">
                <h4>Preview</h4>
                <div className="connection-type-preview">
                  <div 
                    className="connection-type-icon"
                    style={{ backgroundColor: connectionTypeForm.color }}
                  >
                    {connectionTypeForm.icon || '🔗'}
                  </div>
                  <div>
                    <strong>{connectionTypeForm.name || 'Connection Name'}</strong>
                    <p className="connection-type-description">
                      {connectionTypeForm.description || 'Connection description...'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={editingConnectionType ? handleUpdateConnectionType : handleAddConnectionType}
                  className="modal-button-primary"
                >
                  {editingConnectionType ? 'Update' : 'Add'} Connection Type
                </button>
                <button 
                  onClick={() => {
                    setShowConnectionTypeModal(false);
                    resetConnectionTypeForm();
                  }}
                  className="modal-button-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectionTypes;